import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Alert, Button, Popover, Typography } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import { ProductDraftsCountError } from './ProductDraftsCountError';
import type { ProductDraftCountState } from './useProductDraftCount';

export type ProductDraftsNoticeViewProps = {
    /** The draft count's section state (rulebook §12). */
    state: ProductDraftCountState;
    /** The list already shows drafts only, so Show drafts has nothing to do. */
    isDraftsOnly: boolean;
    onShowDrafts: () => void;
};

const COPY = 'products.components.productTable';

/**
 * The drafts notice above the Products toolbar (bos-app#226, US-17, AC-16):
 * how many products an import left as drafts, **Show drafts**, which narrows
 * the list to them, and the help route, "How to fix a draft", which opens a
 * popover with the steps. Not on the canvas yet; the placement and the help
 * route's target are this child's (an in-app popover until a help page
 * exists).
 *
 * Every state renders before its fields are read: nothing while the count
 * loads or when there is no draft (the notice is a pointer, not the list),
 * the danger notice with Try again when the count fails, and the notice with
 * that danger notice below it when a refresh of a shown count fails.
 */
export const ProductDraftsNoticeView: React.FC<
    ProductDraftsNoticeViewProps
> = ({ state, isDraftsOnly, onShowDrafts }) => {
    const { t } = useTranslation('pages');

    if (state.status === 'loading' || state.status === 'empty') {
        return null;
    }
    if (state.status === 'error') {
        return <ProductDraftsCountError onRetry={state.onRetry} />;
    }
    const count = state.data;

    return (
        <>
            <Alert
                status="warning"
                {...getNodeTestIdProp('products-drafts-notice')}
            >
                <Alert.Indicator />
                <Alert.Content>
                    <Alert.Title>
                        {t(`${COPY}.drafts.title`, { count })}
                    </Alert.Title>
                    <Alert.Description>
                        {t(`${COPY}.filters.draftsDescription`)}
                    </Alert.Description>
                    <div className="flex flex-wrap items-center gap-2 pt-2">
                        {isDraftsOnly ? null : (
                            <Button
                                onPress={onShowDrafts}
                                size="sm"
                                variant="secondary"
                                {...getNodeTestIdProp('products-drafts-show')}
                            >
                                <Icon icon="gravity-ui:funnel" />
                                {t(`${COPY}.drafts.show`)}
                            </Button>
                        )}
                        <Popover>
                            <Button
                                size="sm"
                                variant="ghost"
                                {...getNodeTestIdProp('products-drafts-help')}
                            >
                                <Icon icon="gravity-ui:circle-question" />
                                {t(`${COPY}.drafts.help`)}
                            </Button>
                            <Popover.Content
                                className="max-w-80"
                                placement="bottom start"
                            >
                                <Popover.Dialog
                                    {...getNodeTestIdProp(
                                        'products-drafts-help-dialog',
                                    )}
                                >
                                    <Popover.Arrow />
                                    <Popover.Heading>
                                        {t(`${COPY}.drafts.help`)}
                                    </Popover.Heading>
                                    <Typography
                                        className="mt-2"
                                        color="muted"
                                        type="body-sm"
                                    >
                                        {t(`${COPY}.drafts.description`)}
                                    </Typography>
                                </Popover.Dialog>
                            </Popover.Content>
                        </Popover>
                    </div>
                </Alert.Content>
            </Alert>
            {state.refresh === 'error' ? (
                <ProductDraftsCountError onRetry={state.onRetry} />
            ) : null}
        </>
    );
};
